import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { isNull } from "lodash";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  GetTasterExperimentSummaryResponse,
  TasterExperimentsService,
} from "api/client";

import { ROUTES } from "constants/routes";
import { CONSTANTS } from "constants/constants";

const isTokenExpired = (token: string): boolean => {
  try {
    const { exp } = JSON.parse(atob(token.split(".")[1]));
    const now = Date.now() / 1000;
    return exp < now;
  } catch (error) {
    return true; // Assume token is expired if any error occurs
  }
};

const useHomeContainer = () => {
  const [experiments, setExperiments] = useState<
    GetTasterExperimentSummaryResponse[]
  >([]);
  const [activeTab, setActiveTab] = useState<string>("all");
  const [currentExperimentId, setCurrentExperimentId] = useState<string | null>(
    null
  );
  const [currentExperimentToJoinId, setCurrentExperimentToJoinId] = useState<
    string | null
  >(null);
  const [successToast, setSuccessToast] = useState<string | null>(null);
  const [errorToast, setErrorToast] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const { t } = useTranslation(); // Initialize translation

  const token = localStorage.getItem(CONSTANTS.access_token_key);

  const today = useMemo(() => new Date(), []);

  const navigate = useNavigate();

  const fetchExperiments = useCallback(() => {
    setLoading(true);

    TasterExperimentsService.readExperimentsV1TasterExperimentsGet()
      .then((response) =>
        setExperiments(
          response.filter(
            (experiment) =>
              new Date(experiment.schedule.startTime as string) >= today
          )
        )
      )
      .catch((error) => {
        console.log(error, "ERROR");
        setErrorToast(t("error_toast_message"));
      })
      .finally(() => setLoading(false));
  }, [today, t]);

  const fetchJoinedExperiments = useCallback(() => {
    TasterExperimentsService.readExperimentsV1TasterExperimentsGet(true)
      .then((response) => {
        const joinedExperiments = response.filter(
          (experiment) =>
            new Date(experiment.schedule.startTime as string) >= today
        );

        if (joinedExperiments && joinedExperiments.length > 0) {
          navigate(`/experiment/${joinedExperiments[0].id}`);
        } else return;
      })
      .catch(() => setErrorToast(t("error_toast_message")));
  }, [navigate, today, t]);

  useEffect(() => {
    const isTokenValid = token && !isTokenExpired(token);

    if (!token || !isTokenValid) {
      navigate(ROUTES.signIn);
    }
    const handleUnauthorized = () => {
      navigate(ROUTES.signIn);
    };

    // Adding event listener for the unauthorized event
    window.addEventListener("unauthorized", handleUnauthorized);
    if (token) {
      fetchExperiments();
      fetchJoinedExperiments();
    }
  }, [fetchExperiments, token, navigate, fetchJoinedExperiments]);

  useEffect(() => {
    if (token && isNull(currentExperimentId)) {
      const interval = setInterval(() => fetchExperiments(), 10000);

      return () => clearInterval(interval);
    }
  }, [currentExperimentId, fetchExperiments, token]);

  const handleTabsChange = useCallback(
    (value: string) => setActiveTab(value),
    []
  );

  const openExperimentInfoDrawer = useCallback(
    (experimentId: string) => setCurrentExperimentId(experimentId),
    []
  );

  const closeExperimentInfoDrawer = useCallback(
    () => setCurrentExperimentId(null),
    []
  );

  const handleToastClose = useCallback(
    (type: "error" | "success") => () => {
      if (type === "success") {
        setSuccessToast(null);
      } else {
        setErrorToast(null);
      }
    },
    []
  );

  const joinExperiment = useCallback(
    (experimentId: string) => {
      setCurrentExperimentToJoinId(experimentId);

      if (token) {
        setSubmitting(true);

        TasterExperimentsService.joinExperimentV1TasterExperimentsPost(
          experimentId
        )
          .then(() => {
            navigate(`experiment/${experimentId}`);
          })
          .catch(() => {
            navigate(`experiment/${experimentId}`);
          })
          .finally(() => {
            setSubmitting(false);
            setCurrentExperimentToJoinId(null);
          });
      }
    },
    [navigate, token]
  );

  const approveExperiment = useCallback(
    (experimentId: string) => {
      if (token) {
        setSubmitting(true);

        TasterExperimentsService.acceptExperimentV1TasterExperimentsAcceptPost(
          experimentId
        )
          .then(() => {
            console.log("Approved the experiment");
            setSuccessToast(t("approve_experiment_success"));
            fetchExperiments();
          })
          .catch((error) => {
            console.log(error, "ERROR");
            setErrorToast(t("approve_experiment_error"));
          })
          .finally(() => {
            setSubmitting(false);
            closeExperimentInfoDrawer();
          });
      }
    },
    [closeExperimentInfoDrawer, fetchExperiments, token, t]
  );

  const rejectExperiment = useCallback(
    (experimentId: string) => {
      if (token) {
        setSubmitting(true);

        TasterExperimentsService.declineExperimentV1TasterExperimentsDeclinePost(
          experimentId
        )
          .then(() => {
            console.log("Reject the experiment");
            setSuccessToast(t("reject_experiment_success"));
            fetchExperiments();
            const copyExperiments = [...experiments];

            const mappedExperiments = copyExperiments.map((experiment) =>
              experiment.id === experimentId
                ? {
                    ...experiment,
                    isApproved: false,
                  }
                : experiment
            );

            setExperiments(mappedExperiments);
          })
          .catch((error) => {
            console.log(error, "ERROR");
            setErrorToast(t("reject_experiment_error"));
          })
          .finally(() => {
            setSubmitting(false);
            closeExperimentInfoDrawer();
          });
      }
    },
    [closeExperimentInfoDrawer, experiments, fetchExperiments, token]
  );

  const runningExperiments = useMemo(
    () => experiments.filter(({ isApproved }) => isApproved),
    [experiments]
  );

  const filteredExperiments = useMemo(() => {
    if (activeTab === "approved") {
      return experiments.filter(({ isApproved }) => isApproved);
    }

    if (activeTab === "need-approval") {
      return experiments.filter(({ isApproved }) => !isApproved);
    }

    return experiments;
  }, [activeTab, experiments]);

  const currentExperiment = useMemo(
    () => experiments.find(({ id }) => id === currentExperimentId),
    [currentExperimentId, experiments]
  );

  return {
    experiments: filteredExperiments,
    loading,
    submitting,
    runningExperiments,
    activeTab,
    currentExperiment,
    currentExperimentToJoinId,
    errorToast,
    successToast,
    joinExperiment,
    approveExperiment,
    rejectExperiment,
    handleTabsChange,
    openExperimentInfoDrawer,
    closeExperimentInfoDrawer,
    handleToastClose,
  };
};

export default useHomeContainer;
